import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Autocomplete,
  Collapse,
} from "@mui/material";
import {
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  PictureAsPdf as PdfIcon,
  Warning as WarningIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import api, { generateUserPDF } from "../services/api";
import { AVAILABLE_COURSES } from "../constants/courses";

// Get the user API base URL
const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string;
  rollNumber: string;
  secondRollNumber: string;
  email: string;
  fullName: string;
  fatherName: string;
  cnic: string;
  mobile: string;
  dateOfBirth: string;
  gender: "male" | "female";
  qualification: string;
  courses: string[];
  secondEnrolledCourses: string[];
  permanentAddress: string;
  city: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  challans: any;
  challanId?: string | null;
  referralCode?: string;
}

interface UserResponse {
  users: User[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

interface UserStats {
  total: number;
  verified: number;
  unverified: number;
  male: number;
  female: number;
  recent: number;
}

function UserRow(props: {
  user: User;
  getInitials: (name: string) => string;
  formatDate: (date: string) => string;
  handleEditCourses: (user: User, type: "courses" | "secondEnrolledCourses") => void;
  handleEditProfile: (user: User) => void;
  handleGeneratePDF: (user: User) => void;
  handleDeleteUser: (user: User) => void;
}) {
  const { user, getInitials, formatDate, handleEditCourses, handleEditProfile, handleGeneratePDF, handleDeleteUser } = props;
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: open ? 'rgba(241, 245, 249, 0.5)' : 'inherit', transition: 'background-color 0.2s' }}>
        <TableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ ml: 1, color: open ? '#059669' : '#94a3b8', bgcolor: open ? 'rgba(5,150,105,0.1)' : 'transparent' }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ mr: 2, bgcolor: "primary.main", width: 42, height: 42, fontWeight: 700, fontSize: '1rem', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
              {user.fullName ? getInitials(user.fullName) : <PersonIcon />}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem' }}>
                {user.fullName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
            {user.mobile}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
            City: {user.city}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            icon={user.isVerified ? <VerifiedIcon sx={{ fontSize: 16 }} /> : <CancelIcon sx={{ fontSize: 16 }} />}
            label={user.isVerified ? "Verified" : "Pending"}
            size="small"
            sx={{ 
              bgcolor: user.isVerified ? 'rgba(16,185,129,0.1)' : '#fef2f2',
              color: user.isVerified ? '#059669' : '#ef4444',
              fontWeight: 700,
              px: 0.5
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.85rem' }}>
            {Array.isArray(user.courses) && user.courses.length > 0 ? `${user.courses.length} Course(s)` : 'None'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ pr: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpen(!open)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }}
            >
              {open ? 'Hide Details' : 'View Details'}
            </Button>
          </Box>
        </TableCell>
      </TableRow>
      
      {/* Expanded Details Row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 3, bgcolor: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} pb={2} borderBottom="1px solid #e2e8f0">
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  Detailed Student Profile
                </Typography>
                <Box display="flex" gap={1}>
                  <Button size="small" variant="contained" color="primary" startIcon={<EditIcon />} sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }} onClick={() => handleEditProfile(user)}>
                    Edit Info
                  </Button>
                  <Button size="small" variant="contained" sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }} startIcon={<PdfIcon />} onClick={() => handleGeneratePDF(user)}>
                    Challan PDF
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} sx={{ borderRadius: '8px', textTransform: 'none' }} onClick={() => handleDeleteUser(user)}>
                    Delete
                  </Button>
                </Box>
              </Box>

              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
                
                {/* Personal & Academic Info */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, mb: 1.5, fontSize: '0.75rem' }}>
                    Personal Details
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Father's Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{user.fatherName}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">CNIC:</Typography>
                      <Typography variant="body2" fontWeight={600}>{user.cnic}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Gender:</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>{user.gender}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Qualification:</Typography>
                      <Typography variant="body2" fontWeight={600}>{user.qualification}</Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">Permanent Address:</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ bgcolor: '#fff', p: 1, borderRadius: '8px', border: '1px solid #e2e8f0' }}>{user.permanentAddress || 'N/A'}</Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Enrollment & Courses */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, mb: 1.5, fontSize: '0.75rem' }}>
                    Enrollment Status
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Batch 01 Roll No:</Typography>
                      <Chip size="small" label={user.rollNumber} sx={{ fontWeight: 600, height: 22, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                    </Box>
                    {user.secondRollNumber && (
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Batch 02 Roll No:</Typography>
                        <Chip size="small" label={user.secondRollNumber} sx={{ fontWeight: 600, height: 22, bgcolor: '#ede9fe', color: '#5b21b6' }} />
                      </Box>
                    )}
                    {user.referralCode && (
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Referral Code:</Typography>
                        <Chip size="small" label={user.referralCode} sx={{ fontWeight: 700, height: 22, bgcolor: '#fef3c7', color: '#b45309' }} />
                      </Box>
                    )}
                    
                    <Box mt={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight={600} color="#0f172a">Batch 01 Courses</Typography>
                        <IconButton size="small" sx={{ color: '#10b981', bgcolor: 'rgba(16,185,129,0.1)' }} onClick={() => handleEditCourses(user, "courses")}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {Array.isArray(user.courses) && user.courses.length > 0 ? user.courses.map((c, i) => (
                           <Chip key={i} label={c} size="small" sx={{ fontSize: "0.7rem", bgcolor: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 600 }} />
                        )) : <Typography variant="body2" color="text.secondary">None enrolled</Typography>}
                      </Box>
                    </Box>

                    {Array.isArray(user.secondEnrolledCourses) && user.secondEnrolledCourses.length > 0 && (
                      <Box mt={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">Batch 02 Courses</Typography>
                          <IconButton size="small" sx={{ color: '#3b82f6', bgcolor: 'rgba(59,130,246,0.1)' }} onClick={() => handleEditCourses(user, "secondEnrolledCourses")}>
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {user.secondEnrolledCourses.map((c, i) => (
                             <Chip key={i} label={c} size="small" sx={{ fontSize: "0.7rem", bgcolor: 'rgba(59,130,246,0.1)', color: '#2563eb', fontWeight: 600 }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Fees & Dates */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, mb: 1.5, fontSize: '0.75rem' }}>
                    Registration & Fees
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="text.secondary">Fee Status:</Typography>
                        {(Array.isArray(user.challans) && user.challans.some((c:any) => c.paid)) ? (
                          <Chip size="small" label="Paid" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                        ) : (
                          <Chip size="small" label="Unpaid" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                        )}
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Challan IDs:</Typography>
                        <Typography variant="body2" fontWeight={600}>{Array.isArray(user.challans) && user.challans.length > 0 ? user.challans.map((c: any) => c.id || c.challanId).join(", ") : (user.challanId || "N/A")}</Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>Registration Date:</Typography>
                      <Typography variant="body2" fontWeight={600} color="#0f172a">{formatDate(user.createdAt)}</Typography>
                    </Box>

                    {(Array.isArray(user.challans) && user.challans.some((c:any) => c.paid)) && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>Fee Submitted On:</Typography>
                        <Typography variant="body2" fontWeight={600} color="#059669">{formatDate(user.challans.find((c: any) => c.paid)?.updatedAt || user.createdAt)}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [qualification, setQualification] = useState("");
  const [city, setCity] = useState("");
  const [isVerified, setIsVerified] = useState("");
  const [challanPaid, setChallanPaid] = useState("");
  const [noChallan, setNoChallan] = useState("");
  // Course editing states
  const [courseEditDialogOpen, setCourseEditDialogOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(
    null
  );
  const [editedCourses, setEditedCourses] = useState<string[]>([]);
  const [editingCourseType, setEditingCourseType] = useState<
    "courses" | "secondEnrolledCourses"
  >("courses");
  const [newCourse, setNewCourse] = useState("");
  const [courseEditLoading, setCourseEditLoading] = useState(false);
  const [courseEditError, setCourseEditError] = useState("");

  // PDF generation and delete states
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] =
    useState<User | null>(null);
  const [adminApiKey, setAdminApiKey] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Profile edit states
  const [profileEditDialogOpen, setProfileEditDialogOpen] = useState(false);
  const [editProfileData, setEditProfileData] = useState({ fullName: '', email: '', mobile: '' });
  const [profileEditLoading, setProfileEditLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [
    page,
    rowsPerPage,
    search,
    gender,
    qualification,
    city,
    isVerified,
    challanPaid,
    noChallan,
  ]);

  useEffect(() => {
    fetchUserStats();
  }, []);

  // Debug effect for editedCourses
  useEffect(() => {
    if (editedCourses.length > 0) {
      console.log("editedCourses state changed:", editedCourses);
    }
  }, [editedCourses]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) params.search = search;
      if (gender) params.gender = gender;
      if (qualification) params.qualification = qualification;
      if (city) params.city = city;
      if (isVerified !== "") params.isVerified = isVerified;
      if (challanPaid !== "") params.challanPaid = challanPaid;
      if (noChallan !== "") params.noChallan = noChallan;
      const response = await api.get("/users", { params });
      const data: UserResponse = response.data.data;
      setUsers(data.users);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await api.get("/users/stats");
      setStats(response.data.data);
    } catch (err) {
      console.error("Error fetching user stats:", err);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setGender("");
    setQualification("");
    setCity("");
    setIsVerified("");
    setChallanPaid("");
    setNoChallan("");
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-PK", { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getFilterSummary = () => {
    const hasFilters =
      search ||
      gender ||
      qualification ||
      city ||
      isVerified !== "" ||
      challanPaid !== "" ||
      noChallan !== "";
    const totalDbUsers = stats?.total || 0;

    if (!hasFilters) {
      return `Total: ${totalDbUsers.toLocaleString()} users in database`;
    }

    const filterParts = [];
    if (search) filterParts.push(`"${search}"`);
    if (gender) filterParts.push(`${gender}`);
    if (qualification) filterParts.push(`qualification: ${qualification}`);
    if (city) filterParts.push(`city: ${city}`);
    if (isVerified !== "")
      filterParts.push(isVerified === "true" ? "verified" : "unverified");
    if (challanPaid !== "")
      filterParts.push(challanPaid === "true" ? "paid" : "unpaid");
    if (noChallan !== "")
      filterParts.push(noChallan === "true" ? "no challan" : "has challan");
    const filterText =
      filterParts.length > 0 ? ` (${filterParts.join(", ")})` : "";
    return `Showing ${total.toLocaleString()} of ${totalDbUsers.toLocaleString()} users${filterText}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (gender) count++;
    if (qualification) count++;
    if (city) count++;
    if (isVerified !== "") count++;
    if (challanPaid !== "") count++;
    if (noChallan !== "") count++;
    return count;
  };

  const downloadUsers = async () => {
    try {
      setLoading(true);

      // Build the same params as the fetchUsers function
      const params: any = {};

      if (search) params.search = search;
      if (gender) params.gender = gender;
      if (qualification) params.qualification = qualification;
      if (city) params.city = city;
      if (isVerified !== "") params.isVerified = isVerified;
      if (challanPaid !== "") params.challanPaid = challanPaid;
      if (noChallan !== "") params.noChallan = noChallan;
      // Call the download endpoint
      const response = await api.get("/users/download", {
        params,
        responseType: "blob", // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date
      const timestamp = new Date().toISOString().split("T")[0];
      const filterInfo = getActiveFilterCount() > 0 ? "_filtered" : "_all";
      link.download = `users_export${filterInfo}_${timestamp}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading users:", err);
      setError("Failed to download users data");
    } finally {
      setLoading(false);
    }
  };

  // PDF generation handler
  const handleGeneratePDF = (user: User) => {
    setSelectedUserForAction(user);
    setPdfModalOpen(true);
  };

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) {
      alert("Please provide the admin API key");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setPdfGenerating(true);

      const response = await generateUserPDF(
        selectedUserForAction._id,
        adminApiKey
      );
      const data = response.data;

      // Both existing and new challans now return JSON with fileUrl
      if (data.status === "success" && data.data && data.data.fileUrl) {
        // Extract path from fileUrl starting at /uploads
        const fileUrl = data.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");

        // Show appropriate success message
        if (data.message === "Existing challan found") {
          alert(`Existing challan opened: ${data.data.challanNumber}`);
        } else if (data.message === "Challan generated successfully") {
          alert(`New challan generated and opened: ${data.data.challanNumber}`);
        } else {
          alert(`Challan opened: ${data.data.challanNumber}`);
        }
      } else {
        throw new Error("Invalid response format from challan API");
      }

      setPdfModalOpen(false);
      setAdminApiKey("");
    } catch (error: any) {
      console.error("Error generating challan:", error);
      alert(
        `Failed to generate challan: ${error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPdfGenerating(false);
    }
  };

  // User deletion handler
  const handleDeleteUser = (user: User) => {
    setSelectedUserForAction(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteReason.trim()) {
      alert("Please provide a reason for deletion");
      return;
    }

    if (!selectedUserForAction) {
      alert("No user selected");
      return;
    }

    try {
      setLoading(true);

      await api.delete(`/users/${selectedUserForAction._id}`, {
        data: { reason: deleteReason },
      });

      // Refresh the users list
      await fetchUsers();

      // Clear state and close modal
      setSelectedUserForAction(null);
      setDeleteModalOpen(false);
      setDeleteReason("");

      alert("User and all related data deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  // Use stats data instead of current page data
  const verifiedCount = stats?.verified || 0;
  const unverifiedCount = stats?.unverified || 0;

  const handleEditCourses = (
    user: User,
    type: "courses" | "secondEnrolledCourses" = "courses"
  ) => {
    console.log(
      `Opening ${type} edit for user:`,
      user.fullName,
      "Current courses:",
      user[type]
    );

    // Ensure courses is always an array
    const userCourses = Array.isArray(user[type]) ? user[type] : [];
    console.log("Normalized user courses:", userCourses);

    setSelectedUserForEdit(user);
    setEditingCourseType(type);
    setEditedCourses([...userCourses]); // Make sure we copy the array properly
    setNewCourse("");
    setCourseEditError("");
    setCourseEditDialogOpen(true);
  };

  const handleAddCourse = () => {
    console.log("Attempting to add course:", newCourse.trim());
    console.log("Current editedCourses:", editedCourses);
    console.log("Available courses:", AVAILABLE_COURSES);

    if (
      newCourse.trim() &&
      !editedCourses.includes(newCourse.trim()) &&
      AVAILABLE_COURSES.includes(newCourse.trim())
    ) {
      const updatedCourses = [...editedCourses, newCourse.trim()];
      console.log(
        "Adding course:",
        newCourse.trim(),
        "Updated courses:",
        updatedCourses
      );
      setEditedCourses(updatedCourses);
      setNewCourse("");
    } else {
      console.log("Course add validation failed:", {
        hasValue: !!newCourse.trim(),
        alreadyExists: editedCourses.includes(newCourse.trim()),
        isAvailable: AVAILABLE_COURSES.includes(newCourse.trim()),
      });
    }
  };

  const handleRemoveCourse = (courseToRemove: string) => {
    console.log(
      "Removing course:",
      courseToRemove,
      "Current editedCourses:",
      editedCourses
    );
    const updatedCourses = editedCourses.filter(
      (course) => course !== courseToRemove
    );
    console.log("Updated courses after removal:", updatedCourses);
    setEditedCourses(updatedCourses);
  };

  const handleSaveCourses = async () => {
    if (!selectedUserForEdit) return;

    try {
      setCourseEditLoading(true);
      setCourseEditError("");

      console.log("Saving courses for user:", selectedUserForEdit._id);
      console.log("Type:", editingCourseType);
      console.log("Original courses:", selectedUserForEdit[editingCourseType]);
      console.log("New courses:", editedCourses);

      const payload = {
        [editingCourseType]: editedCourses,
        reason: "Admin updated user courses",
      };

      console.log("API payload:", payload);

      const response = await api.put(
        `/users/${selectedUserForEdit._id}`,
        payload
      );
      console.log("API response:", response.data);

      // Update the user in the local state
      setUsers(
        users.map((user) =>
          user._id === selectedUserForEdit._id
            ? { ...user, [editingCourseType]: editedCourses }
            : user
        )
      );

      setCourseEditDialogOpen(false);
      setSelectedUserForEdit(null);
      setEditedCourses([]);
    } catch (err) {
      console.error("Error updating user courses:", err);
      setCourseEditError("Failed to update courses. Please try again.");
    } finally {
      setCourseEditLoading(false);
    }
  };

  const handleCloseCourseDialog = () => {
    setCourseEditDialogOpen(false);
    setSelectedUserForEdit(null);
    setEditedCourses([]);
    setNewCourse("");
    setCourseEditError("");
  };

  const handleEditProfile = (user: User) => {
    setSelectedUserForEdit(user);
    setEditProfileData({ fullName: user.fullName, email: user.email, mobile: user.mobile });
    setProfileEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!selectedUserForEdit) return;
    try {
      setProfileEditLoading(true);
      const payload = {
        fullName: editProfileData.fullName,
        email: editProfileData.email,
        mobile: editProfileData.mobile,
        reason: "Admin updated user profile from front panel",
      };
      await api.put(`/users/${selectedUserForEdit._id}`, payload);
      setUsers(
        users.map((user) =>
          user._id === selectedUserForEdit._id
            ? { ...user, fullName: editProfileData.fullName, email: editProfileData.email, mobile: editProfileData.mobile }
            : user
        )
      );
      setProfileEditDialogOpen(false);
      setSelectedUserForEdit(null);
    } catch (err) {
      console.error("Error updating profile:", err);
      alert("Failed to update profile. Please try again.");
    } finally {
      setProfileEditLoading(false);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Users Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getFilterSummary()}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Compact Summary Cards matching Dashboard style */}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={2} mb={3}>
        {[
          {
            title: 'Verified Users',
            value: verifiedCount.toLocaleString(),
            subtitle: `Success Rate: ${stats ? Math.round((stats.verified / stats.total) * 100) : 0}%`,
            icon: <VerifiedIcon />,
            color: '#10b981',
            bg: 'rgba(16,185,129,0.08)',
            delay: 0.1
          },
          {
            title: 'Unverified Users',
            value: unverifiedCount.toLocaleString(),
            subtitle: 'Pending Verification',
            icon: <CancelIcon />,
            color: '#f59e0b',
            bg: 'rgba(245,158,11,0.08)',
            delay: 0.2
          },
          {
            title: 'Available Courses',
            value: AVAILABLE_COURSES.length.toLocaleString(),
            subtitle: 'Active Programs',
            icon: <SchoolIcon />,
            color: '#3b82f6',
            bg: 'rgba(59,130,246,0.08)',
            delay: 0.3
          }
        ].map((item) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: item.delay }}
          >
            <Card sx={{
              borderRadius: '16px',
              bgcolor: 'white',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
              position: 'relative',
              overflow: 'hidden',
              height: '100%'
            }}>
              <Box sx={{ height: 3, bgcolor: item.color }} />
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: item.bg,
                    color: item.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon as React.ReactElement<any>, { sx: { fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      {/* Redesigned Filters Section */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
        <Paper sx={{
          p: 3,
          mb: 4,
          borderRadius: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                p: 1,
                borderRadius: '12px',
                bgcolor: '#eff6ff',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FilterIcon fontSize="small" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Search Filters</Typography>
              {getActiveFilterCount() > 0 && (
                <Chip
                  label={`${getActiveFilterCount()} active`}
                  size="small"
                  sx={{
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 600,
                    borderRadius: '8px'
                  }}
                />
              )}
            </Box>

            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                startIcon={<CancelIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none' }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={downloadUsers}
                disabled={loading}
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: '10px', textTransform: 'none', boxShadow: 'none' }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }} gap={2.5}>
            <TextField
              fullWidth
              label="Global Search"
              placeholder="Name, Email, Roll No..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 20 }} />,
                sx: { borderRadius: '12px' }
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={gender}
                label="Gender"
                onChange={(e) => setGender(e.target.value as string)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">All Genders</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={challanPaid}
                label="Payment Status"
                onChange={(e) => setChallanPaid(e.target.value as string)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">All Payments</MenuItem>
                <MenuItem value="false">Unpaid</MenuItem>
                <MenuItem value="true">Paid</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Challan Status</InputLabel>
              <Select
                value={noChallan}
                label="Challan Status"
                onChange={(e) => setNoChallan(e.target.value as string)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">No Challan</MenuItem>
                <MenuItem value="false">Has Challan</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Search by city"
              InputProps={{ sx: { borderRadius: '12px' } }}
            />

            <TextField
              fullWidth
              label="Qualification"
              value={qualification}
              onChange={(e) => setQualification(e.target.value)}
              placeholder="e.g. Bachelor"
              InputProps={{ sx: { borderRadius: '12px' } }}
            />

            <FormControl fullWidth>
              <InputLabel>Verification</InputLabel>
              <Select
                value={isVerified}
                label="Verification"
                onChange={(e) => setIsVerified(e.target.value as string)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">All Users</MenuItem>
                <MenuItem value="true">Verified</MenuItem>
                <MenuItem value="false">Unverified</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {getActiveFilterCount() > 0 && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Found {total.toLocaleString()} matching records
              </Typography>
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell width={40} />
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Student Profile</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Contact & City</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Verification</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Courses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <UserRow 
                    key={user._id} 
                    user={user} 
                    getInitials={getInitials} 
                    formatDate={formatDate}
                    handleEditCourses={handleEditCourses}
                    handleEditProfile={handleEditProfile}
                    handleGeneratePDF={handleGeneratePDF}
                    handleDeleteUser={handleDeleteUser}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Course Edit Dialog */}
      <Dialog
        open={courseEditDialogOpen}
        onClose={handleCloseCourseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Edit{" "}
            {editingCourseType === "courses"
              ? "Courses"
              : "Second Enrollment Courses"}{" "}
            for {selectedUserForEdit?.fullName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {courseEditError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {courseEditError}
              </Alert>
            )}

            {/* Debug info */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Debug Info:</strong> Current editedCourses:{" "}
              {JSON.stringify(editedCourses)}
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Current Courses ({editedCourses.length})
              </Typography>
              {editedCourses.some(
                (course) => !AVAILABLE_COURSES.includes(course)
              ) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some courses are not in the current available courses list.
                    These will be preserved but cannot be re-added if removed.
                  </Alert>
                )}
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {editedCourses.map((course, index) => (
                  <Chip
                    key={index}
                    label={course}
                    onDelete={() => handleRemoveCourse(course)}
                    deleteIcon={<DeleteIcon />}
                    color={
                      AVAILABLE_COURSES.includes(course) ? "primary" : "default"
                    }
                    variant="outlined"
                  />
                ))}
                {editedCourses.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No courses assigned
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Autocomplete
                fullWidth
                value={null}
                onChange={(event, newValue) => {
                  if (newValue && !editedCourses.includes(newValue)) {
                    setEditedCourses([...editedCourses, newValue]);
                    setNewCourse("");
                  }
                }}
                inputValue={newCourse}
                onInputChange={(event, newInputValue) => {
                  setNewCourse(newInputValue);
                }}
                options={AVAILABLE_COURSES.filter(
                  (course) => !editedCourses.includes(course)
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Course to Add"
                    placeholder="Choose a course"
                    helperText="Selecting a course adds it immediately to the list above"
                  />
                )}
                noOptionsText="No more courses available"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Changes will be logged for audit purposes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Available Courses:</strong> {AVAILABLE_COURSES.length}{" "}
              predefined courses available for selection.
            </Typography>

            {/* Debug button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const testCourse = AVAILABLE_COURSES[0];
                if (testCourse && !editedCourses.includes(testCourse)) {
                  setEditedCourses([...editedCourses, testCourse]);
                }
              }}
            >
              Test Add Course
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCourseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCourses}
            variant="contained"
            disabled={courseEditLoading}
            startIcon={
              courseEditLoading ? <CircularProgress size={16} /> : <EditIcon />
            }
          >
            {courseEditLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Generation Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon color="info" sx={{ mr: 1 }} />
            Generate Challan PDF
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Challan Generation:</strong> This will generate or open the
            challan PDF for the selected user in a new tab. If a challan already
            exists, it will be opened. If not, a new one will be created and
            opened.
          </Alert>
          {selectedUserForAction && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedUserForAction.fullName}
              <br />
              <strong>Roll Number:</strong> {selectedUserForAction.rollNumber}
              <br />
              <strong>Email:</strong> {selectedUserForAction.email}
            </Alert>
          )}
          <TextField
            label="Admin API Key (Required)"
            placeholder="Enter your admin API key..."
            value={adminApiKey}
            onChange={(e) => setAdminApiKey(e.target.value)}
            fullWidth
            type="password"
            required
            helperText="This API key is required to authenticate the PDF generation request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmGeneratePDF}
            variant="contained"
            color="info"
            disabled={!adminApiKey.trim() || pdfGenerating}
            startIcon={
              pdfGenerating ? <CircularProgress size={20} /> : <PdfIcon />
            }
          >
            {pdfGenerating
              ? "Processing Challan..."
              : "Generate & Open Challan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Confirm User Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>DANGER:</strong> This will permanently delete the user and
            ALL related data including:
            <ul>
              <li>User account information</li>
              <li>All associated challans</li>
              <li>All associated scholarships</li>
            </ul>
            This action cannot be undone!
          </Alert>
          {selectedUserForAction && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>User to be deleted:</strong>
              <br />
              Name: {selectedUserForAction.fullName}
              <br />
              Email: {selectedUserForAction.email}
              <br />
              Roll Number: {selectedUserForAction.rollNumber}
            </Alert>
          )}
          <TextField
            label="Reason for Deletion (Required)"
            placeholder="Explain why this user is being deleted..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteUser}
            variant="contained"
            color="error"
            disabled={!deleteReason.trim() || loading}
            startIcon={<DeleteIcon />}
          >
            Delete User & All Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog
        open={profileEditDialogOpen}
        onClose={() => setProfileEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Edit Profile for {selectedUserForEdit?.fullName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={editProfileData.fullName}
              onChange={(e) => setEditProfileData({ ...editProfileData, fullName: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              value={editProfileData.email}
              onChange={(e) => setEditProfileData({ ...editProfileData, email: e.target.value })}
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={editProfileData.mobile}
              onChange={(e) => setEditProfileData({ ...editProfileData, mobile: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={profileEditLoading || !editProfileData.fullName || !editProfileData.email || !editProfileData.mobile}
          >
            {profileEditLoading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
